import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Clock, Star, Ticket } from "lucide-react";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1661159245219-f1768c7e98c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

function ActivityCard({ a, idx }) {
  const navigate = useNavigate();
  const { formatPrice } = useApp();
  const mods = a.modalities?.filter((m) => m.amount != null) || [];
  const [mod, setMod] = useState(mods[0]?.code || "");
  const selected = mods.find((m) => m.code === mod);
  const price = selected?.amount ?? a.amount;

  const book = () => navigate("/checkout", { state: { item: {
    productType: "activity",
    title: a.name,
    subtitle: selected ? selected.name : (a.summary || ""),
    image: a.image || FALLBACK_IMG,
    priceEur: price,
    meta: { code: a.code, modality: selected?.code, modalityName: selected?.name, duration: selected?.duration },
  } } });

  return (
    <div data-testid={`activity-card-${idx}`} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden flex flex-col transition-transform duration-300 hover:-translate-y-1">
      <div className="h-48 bg-[#E2E8F0]">
        <img src={a.image || FALLBACK_IMG} alt={a.name} className="w-full h-full object-cover" onError={(e) => { e.target.src = FALLBACK_IMG; }} />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <span className="label-caps">{a.category || "Experience"}</span>
        <h3 className="font-display text-lg font-bold text-[#0B2038] leading-tight mt-1 line-clamp-2">{a.name}</h3>
        <p className="text-[#475569] text-sm mt-2 line-clamp-2 flex-1">{a.summary || a.description}</p>
        {mods.length > 1 && (
          <Select value={mod} onValueChange={setMod}>
            <SelectTrigger data-testid={`activity-modality-${idx}`} className="mt-3 h-9 text-xs border-[#E2E8F0]"><SelectValue placeholder="Choose option" /></SelectTrigger>
            <SelectContent>
              {mods.map((m, mi) => <SelectItem key={`${m.code}-${mi}`} value={m.code} className="text-xs">{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E2E8F0]">
          <div>
            <p className="text-xs text-[#475569]">from</p>
            <p className="font-display text-xl font-bold text-[#0B2038]">{formatPrice(price)}</p>
          </div>
          <Button data-testid={`activity-book-${idx}`} onClick={book} className="rounded-full bg-[#009E9E] hover:bg-[#007A7A] text-white font-semibold h-9 px-5 text-sm">Book</Button>
        </div>
      </div>
    </div>
  );
}

export default function ActivityResults() {
  const [params] = useSearchParams();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const search = {
    destination: params.get("destination") || "IST",
    fromDate: params.get("fromDate") || new Date(Date.now() + 21 * 864e5).toISOString().slice(0, 10),
    toDate: params.get("toDate") || new Date(Date.now() + 28 * 864e5).toISOString().slice(0, 10),
  };

  useEffect(() => {
    setLoading(true);
    api.post("/activities/search", search).then((r) => setActivities(r.data.activities)).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [params]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="pt-24 max-w-7xl mx-auto px-5 lg:px-8">
        <span className="label-caps">Things to do</span>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-[#0B2038] mb-6 mt-1">
          {loading ? "Finding experiences…" : `${activities.length} experiences`}
        </h1>
        {loading ? (
          <div className="flex flex-col items-center py-24 text-[#475569]"><Loader2 className="w-8 h-8 animate-spin mb-3 text-[#009E9E]" />Loading activities…</div>
        ) : activities.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 text-center text-[#475569]">No activities found for these dates.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {activities.map((a, i) => <ActivityCard key={`${a.code}-${i}`} a={a} idx={i} />)}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
